import { formatCrmMoney, formatCrmPriceLabel } from './crm-money.js';
import {
  CrmRevisionConflictEnvelope,
  Session as SessionSchema,
  UpdateSessionRequest as UpdateSessionSchema,
  SESSION_STATUS_LABELS,
  type UpdateSessionRequest,
} from '@diana/contracts';
import { AdminApiError } from '../../lib/api-client.js';
import { sessionDraft } from './crm-format.js';

export type ShootDraft = Omit<UpdateSessionRequest, 'expectedRevision'>;
export type ShootComparison = {
  current: ShootDraft;
  submitted: ShootDraft;
  // Keep the raw attempt: API trimming must not look like later local typing.
  localSubmitted: ShootDraft;
};
const fields = [
  ['transactions', 'Financial transactions'],
  ['title', 'Shoot title'],
  ['isGiftCard', 'Gift card'],
  ['status', 'Status'],
  ['sessionType', 'Shoot type'],
  ['scheduledStartAt', 'Start date and time'],
  ['scheduledEndAt', 'End date and time'],
  ['location', 'Location'],
  ['priceAmountMinor', 'Price'],
  ['expensesAmountMinor', 'Expenses'],
  ['currency', 'Currency'],
  ['notes', 'Client message'],
  ['internalNotes', 'Notes'],
  ['galleryUrl', 'Gallery link'],
  ['requestedPackageKey', 'Package key'],
  ['requestedPackageTitle', 'Package title'],
  ['requestedPackagePriceLabel', 'Package price label'],
  ['sourcePath', 'Page'],
  ['clients', 'Linked clients'],
  ['checklist', 'Checklist'],
] as const satisfies ReadonlyArray<readonly [keyof ShootDraft, string]>;

function normalizeRequest(request: UpdateSessionRequest): UpdateSessionRequest {
  const parsed = UpdateSessionSchema.parse(request);
  return {
    ...parsed,
    transactions: parsed.transactions ?? [],
    isGiftCard: parsed.isGiftCard ?? false,
    internalNotes: parsed.internalNotes ?? null,
    galleryUrl: parsed.galleryUrl ?? null,
    scheduledStartAt:
      parsed.scheduledStartAt === null ? null : new Date(parsed.scheduledStartAt).toISOString(),
    scheduledEndAt:
      parsed.scheduledEndAt === null ? null : new Date(parsed.scheduledEndAt).toISOString(),
    clients: [...parsed.clients].sort((left, right) => left.position - right.position),
    checklist: [...parsed.checklist].sort((left, right) => left.position - right.position),
    requestedPackageKey: parsed.requestedPackageKey ?? null,
    requestedPackageTitle: parsed.requestedPackageTitle ?? null,
    requestedPackagePriceLabel: parsed.requestedPackagePriceLabel ?? null,
    sourcePath: parsed.sourcePath ?? null,
  };
}
function draftFromRequest({
  expectedRevision: _revision,
  ...draft
}: UpdateSessionRequest): ShootDraft {
  return draft;
}
function sameFields(left: ShootDraft, right: ShootDraft) {
  return fields.every(([key]) => JSON.stringify(left[key]) === JSON.stringify(right[key]));
}

export function readShootConflict(error: unknown, shootId: string, attempted: unknown) {
  if (!(error instanceof AdminApiError) || error.code !== 'REVISION_CONFLICT') return null;
  const parsed = CrmRevisionConflictEnvelope.safeParse(error.payload);
  if (!parsed.success) return null;
  const envelope = parsed.data.error;
  const current = SessionSchema.safeParse(envelope.current);
  const submitted = UpdateSessionSchema.safeParse(envelope.submitted);
  const captured = UpdateSessionSchema.safeParse(attempted);
  if (
    !current.success ||
    !submitted.success ||
    !captured.success ||
    current.data.id !== shootId ||
    current.data.revision !== envelope.currentRevision ||
    envelope.currentRevision <= envelope.expectedRevision ||
    envelope.expectedRevision !== submitted.data.expectedRevision ||
    submitted.data.expectedRevision !== captured.data.expectedRevision
  )
    return null;
  const normalizedSubmitted = normalizeRequest(submitted.data);
  const normalizedCaptured = normalizeRequest(captured.data);
  if (!sameFields(normalizedCaptured, normalizedSubmitted)) return null;
  return {
    serverValue: sessionDraft(current.data),
    serverRevision: envelope.currentRevision,
    current: current.data,
    comparison: {
      current: sessionDraft(current.data),
      submitted: draftFromRequest(submitted.data),
      localSubmitted: draftFromRequest(structuredClone(attempted as UpdateSessionRequest)),
    } satisfies ShootComparison,
  };
}

export function hasLaterShootEdits(comparison: ShootComparison, latest: ShootDraft) {
  return !sameFields(comparison.localSubmitted, latest);
}

export function shootDifferences(
  comparison: ShootComparison,
  clientNames: ReadonlyMap<string, string> = new Map(),
) {
  const current = normalizeRequest({ ...comparison.current, expectedRevision: 0 });
  const submitted = normalizeRequest({ ...comparison.submitted, expectedRevision: 0 });
  function display(key: keyof ShootDraft, draft: ShootDraft): string {
    const value = draft[key];
    if (value === null || value === undefined || value === '') return 'Empty';
    if (key === 'requestedPackagePriceLabel')
      return formatCrmPriceLabel(value as string) ?? 'Empty';
    if (key === 'isGiftCard') return value ? 'Yes' : 'No';
    if (key === 'status') return SESSION_STATUS_LABELS[draft.status];
    if (key === 'scheduledStartAt' || key === 'scheduledEndAt')
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
        timeZoneName: 'short',
      }).format(new Date(value as string));
    if (key === 'priceAmountMinor' || key === 'expensesAmountMinor')
      return formatCrmMoney(value as number, draft.currency, true);
    if (key === 'transactions')
      return (
        (draft.transactions ?? [])
          .map(
            (item) =>
              `${item.date} | ${item.kind} | ${formatCrmMoney(item.amountMinor, draft.currency, true)} | ${item.purpose} | ${item.description}`,
          )
          .join('\n') || 'No transactions'
      );
    if (key === 'clients')
      return (
        draft.clients
          .map(
            (client) =>
              `${clientNames.has(client.clientId) ? `${clientNames.get(client.clientId)} (${client.clientId})` : client.clientId} — ${client.isPrimary ? 'Primary' : 'Additional'}, position ${client.position}`,
          )
          .join('\n') || 'Empty'
      );
    if (key === 'checklist')
      return (
        draft.checklist
          .map(
            (item) =>
              `${item.title} — ${item.completed ? 'Completed' : 'Incomplete'}, position ${item.position} (${item.id})`,
          )
          .join('\n') || 'Empty'
      );
    return String(value);
  }
  return fields
    .filter(([key]) => JSON.stringify(current[key]) !== JSON.stringify(submitted[key]))
    .map(([key, label]) => ({
      key,
      label,
      current: display(key, current),
      submitted: display(key, submitted),
    }));
}
